import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  IRoute,
  RouteService,
  TokenService,
  UserService
} from 'viggo-sistemas-core-web';

export interface IFastMenuItem {
  icon: string;
  title: string;
  route: string[];
  backend_config?: Partial<IRoute>[];
}

const ordemAlfabetica = (a: IFastMenuItem, b: IFastMenuItem) =>
  a.title > b.title ? 1 : -1;

@Injectable({
  providedIn: 'root'
})
export class FastMenuService {
  private fastMenuSource = new Subject<string>();
  private resetSource = new Subject();

  public constructor(
    protected userService: UserService,
    protected routeService: RouteService,
    protected tokenService: TokenService
  ) {}

  fastMenuSource$ = this.fastMenuSource.asObservable();
  resetSource$ = this.resetSource.asObservable();

  public onCreateFastMenu(): Observable<IFastMenuItem[]> {
    // checks user is logged
    if (!this.tokenService.isLoggedIn()) return of([]);
    return this.userService.getRoutes().pipe(
      tap(routes => {
        this.tokenService.setRoutes(routes);
      }),
      map(routes => {
        return this.createFastMenu(routes);
      })
    );
  }

  protected createFastMenu(routes: IRoute[]): IFastMenuItem[] {
    let menu: IFastMenuItem[] = [];

    if (!this.tokenService.isLoggedIn() && menu.length !== 0) return menu;

    const dymamicsMenus = this.createDynamicsFastMenu(routes);
    if (dymamicsMenus && dymamicsMenus.length) menu = [...dymamicsMenus];

    menu.unshift({
      title: 'Início',
      icon: 'pi pi-fw pi-home',
      route: ['/']
    });

    return menu;
  }

  protected createDynamicsFastMenu(routes: IRoute[]): IFastMenuItem[] {
    let submenuSysadmin: IFastMenuItem[];
    let submenuAdmin: IFastMenuItem[];

    const menu: Array<IFastMenuItem> = [];

    if (routes.length !== 0) {
      submenuSysadmin = this.createSysadminFastMenu(routes);
      submenuAdmin = this.createAdminFastMenu(routes);
    }

    if (
      submenuSysadmin.length &&
      this.tokenService.getDomain().name === 'default'
    ) {
      submenuSysadmin.forEach(it => {
        menu.push(it);
      });
    }

    if (submenuAdmin.length) {
      submenuAdmin.forEach(it => {
        menu.push(it);
      });
    }

    return menu;
  }

  protected buildFastMenu(
    routes: IRoute[],
    itens: IFastMenuItem[]
  ): IFastMenuItem[] {
    const menu: IFastMenuItem[] = [];
    const urlsGrants = [];
    itens.forEach(it => {
      urlsGrants.push(it.backend_config[0]);
    });
    const grantURLs = this.getGrantURLs(routes, urlsGrants);
    grantURLs.forEach(rota => {
      const itemIndex = itens.findIndex(
        v =>
          v.backend_config[0].name === rota.url &&
          v.backend_config[0].method === rota.method
      );
      if (itemIndex >= 0) menu.push(itens[itemIndex]);
    });
    return menu;
  }

  protected getGrantURLs(routes: IRoute[], urls: any[]): IRoute[] {
    const grantURLs: IRoute[] = [];
    routes.forEach(route => {
      urls.forEach(url => {
        if (url.name === route.url && url.method === route.method) {
          grantURLs.push(route);
        }
      });
    });
    return grantURLs;
  }

  protected createSysadminFastMenu(routes: IRoute[]): IFastMenuItem[] {
    return this.tokenService.getUser().name === 'sysadmin'
      ? this.createFastMenuSysadmin(routes).sort(ordemAlfabetica)
      : this.createFastMenuSuporte(routes).sort(ordemAlfabetica);
  }

  protected createAdminFastMenu(routes: IRoute[]) {
    return localStorage.getItem('INTEGRACAO') === 'true'
      ? this.createFastMenuAdminIntegracao(routes).sort(ordemAlfabetica)
      : this.createFastMenuAdmin(routes).sort(ordemAlfabetica);
  }

  protected createFastMenuAdminIntegracao(routes: IRoute[]) {
    return this.buildFastMenu(routes, [
      {
        title: 'Vendedores',
        icon: 'pi pi-users',
        route: ['/vendedor'],
        backend_config: [
          {
            name: '/vendedores',
            method: 'GET'
          }
        ]
      },
      {
        title: 'Catálogos',
        icon: 'pi pi-cart-plus',
        route: ['/catalogo-produto'],
        backend_config: [
          {
            name: '/catalogos/<id>',
            method: 'PUT'
          }
        ]
      },
      {
        title: 'Produtos',
        icon: 'pi pi-box',
        route: ['/produtos'],
        backend_config: [
          {
            name: '/produtos',
            method: 'GET'
          }
        ]
      },
      {
        title: 'Grupos',
        icon: 'pi pi-box',
        route: ['/grupos'],
        backend_config: [
          {
            name: '/grupos',
            method: 'GET'
          }
        ]
      }
    ]);
  }

  protected createFastMenuAdmin(routes: IRoute[]) {
    return this.buildFastMenu(routes, [
      {
        title: 'Clientes',
        icon: 'pi pi-users',
        route: ['/cliente'],
        backend_config: [
          {
            name: '/clientes',
            method: 'GET'
          }
        ]
      },
      {
        title: 'Pedidos',
        icon: 'pi pi-shopping-cart',
        route: ['/pedidos'],
        backend_config: [
          {
            name: '/pedidos',
            method: 'GET'
          }
        ]
      },
      {
        title: 'Produtos',
        icon: 'pi pi-box',
        route: ['/produtos'],
        backend_config: [
          {
            name: '/produtos',
            method: 'GET'
          }
        ]
      },
      {
        title: 'Movimentação financeira',
        icon: 'pi pi-money-bill',
        route: ['/movimentacao_financeira'],
        backend_config: [
          {
            name: '/movimentacao_financeiras',
            method: 'GET'
          }
        ]
      },
      {
        title: 'Contas a receber',
        icon: 'pi pi-money-bill',
        route: ['/contas-receber'],
        backend_config: [
          {
            name: '/conta_recebers',
            method: 'GET'
          }
        ]
      }
    ]);
  }

  protected createFastMenuSysadmin(routes: IRoute[]) {
    return this.buildFastMenu(routes, [
      {
        title: 'Aplicações',
        icon: 'pi pi-building',
        route: ['/application'],
        backend_config: [
          {
            name: '/applications',
            method: 'POST'
          }
        ]
      },
      {
        title: 'Rotas',
        icon: 'pi pi-directions',
        route: ['/route'],
        backend_config: [
          {
            name: '/routes',
            method: 'POST'
          }
        ]
      },
      {
        title: 'Usuários',
        icon: 'pi pi-users',
        route: ['/user'],
        backend_config: [
          {
            name: '/users',
            method: 'POST'
          }
        ]
      },
      {
        title: 'Organizações',
        icon: 'pi pi-briefcase',
        route: ['/domain'],
        backend_config: [
          {
            name: '/domains',
            method: 'POST'
          }
        ]
      }
    ]);
  }

  protected createFastMenuSuporte(routes: IRoute[]) {
    const menuSup = this.buildFastMenu(routes, [
      {
        title: 'Sub-rotinas',
        icon: 'pi pi-code',
        route: ['/iniciar_subrotina'],
        backend_config: [
          {
            method: 'POST',
            name: '/integracoes/inicia_subrotina'
          }
        ]
      },
      {
        title: 'Usuários',
        icon: 'pi pi-users',
        route: ['/user'],
        backend_config: [
          {
            name: '/users',
            method: 'GET'
          }
        ]
      },
      {
        title: 'Organizações',
        icon: 'pi pi-briefcase',
        route: ['/domain'],
        backend_config: [
          {
            name: '/domains',
            method: 'GET'
          }
        ]
      }
    ]);
    menuSup.push({
      title: 'Usuários por domínio',
      icon: 'pi pi-users',
      route: ['/support-user']
    });
    return menuSup;
  }
}
